/**
 * «Exclusión al entrar» (dirección, 31/10/2026), desde Campañas.
 *
 * Qué contactos del formulario de Meta entran en Leads YA DESCARTADOS: los
 * que cumplen alguna regla (de su campaña o de todas). Se importan igual
 * —cuentan en su campaña, como cualquier descartado— pero no salen en
 * Pendientes ni avisan, y se recuperan desde «Descartados». Se decide al
 * entrar: lo que ya está en Leads no se toca. La web, el correo y lo apuntado
 * a mano no pasan por aquí.
 *
 * Las reglas son DATOS de la clínica (las guarda el servidor,
 * services/exclusion_leads.py), con las piezas del orden de Pendientes
 * (components/ReglasContactos.jsx); cada una puede llevar el motivo que se lee
 * en «Descartados». La vista previa dice, con las reglas que se están
 * editando, cuántos de los contactos de Meta de los últimos 60 días se habrían
 * excluido (sin guardar nada). Solo administradores.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDateTime } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Loader2, Plus } from 'lucide-react';
import {
  EditorRegla, copiaReglas, mensajeError, problemaDeLasReglas, reglaNueva,
} from './ReglasContactos';

const ESPERA_VISTA_PREVIA_MS = 400;

/** La vista previa en una frase. */
export function textoVistaPrevia(previa) {
  if (!previa) return '';
  return `De los ${previa.contactos} contacto(s) de Meta de los últimos ${previa.dias} días, `
    + `con estas reglas se habrían excluido ${previa.excluidos}.`;
}

export default function ExclusionContactosDialog({ onClose }) {
  const [datos, setDatos] = useState(null);
  const [fallo, setFallo] = useState('');
  const [reglas, setReglas] = useState([]);
  const [tocadas, setTocadas] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get('/api/campanas/exclusion')
      .then((r) => {
        if (!vivo) return;
        setDatos(r.data || {});
        setReglas(copiaReglas(r.data?.reglas));
        setPrevia(r.data?.previa || null);
      })
      .catch((e) => { if (vivo) setFallo(mensajeError(e, 'No se han podido leer las reglas')); });
    return () => { vivo = false; };
  }, []);

  const campos = useMemo(() => datos?.campos || [], [datos]);
  const procedencias = datos?.procedencias || [];
  const problema = problemaDeLasReglas(reglas, '');

  // La vista previa con lo que se está editando: un poco después del último
  // cambio, y solo si las reglas se pueden guardar. No escribe nada.
  useEffect(() => {
    if (!tocadas || problema || !datos) return undefined;
    let vivo = true;
    const espera = setTimeout(() => {
      setCalculando(true);
      api.post('/api/campanas/exclusion/previa', { reglas })
        .then((r) => { if (vivo) setPrevia(r.data || null); })
        .catch((e) => { if (vivo) toast.error(mensajeError(e, 'No se ha podido calcular la vista previa')); })
        .finally(() => { if (vivo) setCalculando(false); });
    }, ESPERA_VISTA_PREVIA_MS);
    return () => { vivo = false; clearTimeout(espera); };
  }, [reglas, tocadas, problema, datos]);

  const cambiar = (fn) => {
    setTocadas(true);
    setReglas((antes) => { const nuevas = copiaReglas(antes); fn(nuevas); return nuevas; });
  };

  const guardar = async () => {
    if (guardando || problema) return;
    setGuardando(true);
    try {
      const r = await api.put('/api/campanas/exclusion',
        { reglas, actualizado_at: datos?.actualizado?.at ?? null });
      toast.success(r.data?.sin_cambios ? 'No había nada que cambiar'
        : 'Exclusión guardada: vale para los contactos que entren a partir de ahora');
      onClose();
    } catch (e) {
      toast.error(mensajeError(e, 'No se han podido guardar las reglas'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(abierta) => { if (!abierta) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="exclusion-dialogo">
        <DialogHeader><DialogTitle>Exclusión al entrar</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          Los contactos del formulario de Meta que cumplen alguna regla entran en Leads
          {' '}<strong>ya descartados</strong>: cuentan en su campaña, pero no salen en Pendientes ni
          avisan, y se recuperan desde «Descartados». Dentro de una regla se tienen que cumplir
          {' '}<strong>todas</strong> sus condiciones; si el contacto no contesta una pregunta (su formulario
          no la hace), esa condición no se cumple y entra como siempre. Vale para los que entren a
          partir de ahora: los que ya están no se tocan. La web, el correo y lo apuntado a mano entran
          siempre.
        </p>
        {fallo ? (
          <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3"
            data-testid="exclusion-fallo">{fallo}</div>
        ) : !datos ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="space-y-3">
            {!reglas.length ? (
              <p className="text-sm text-muted-foreground" data-testid="exclusion-sin-reglas">
                Sin reglas: no se excluye a nadie, entran todos como siempre.
              </p>
            ) : null}
            {reglas.map((r, i) => (
              <EditorRegla key={r.id || `nueva-${i}`} regla={r} campos={campos} procedencias={procedencias}
                base={`exclusion-regla-${i}`} titulo={i === 0 ? `Regla ${i + 1}` : `O bien, regla ${i + 1}`}
                conMotivo
                onChange={(nueva) => cambiar((rs) => { rs[i] = nueva; })}
                onQuitar={() => cambiar((rs) => { rs.splice(i, 1); })} />
            ))}
            <Button type="button" size="sm" variant="outline" data-testid="exclusion-anadir-regla"
              onClick={() => cambiar((rs) => { rs.push(reglaNueva(campos)); })}>
              <Plus className="w-4 h-4 mr-1" /> Añadir regla
            </Button>
            {previa ? (
              <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1" data-testid="exclusion-previa">
                <div className="font-medium flex items-center gap-2">
                  Vista previa
                  {calculando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                </div>
                <p data-testid="exclusion-previa-texto">{textoVistaPrevia(previa)}</p>
                {(previa.por_procedencia || []).map((f) => (
                  <p key={f.clave} className="text-xs text-muted-foreground"
                    data-testid={`exclusion-previa-${f.clave}`}>
                    {f.nombre}: {f.excluidos} de {f.contactos}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        )}
        {datos?.actualizado?.at ? (
          <p className="text-xs text-muted-foreground" data-testid="exclusion-actualizado">
            Cambiadas por última vez el {formatDateTime(datos.actualizado.at)}
            {datos.actualizado.por_nombre ? ` por ${datos.actualizado.por_nombre}` : ''}.
          </p>
        ) : null}
        {problema ? (
          <p className="text-xs text-amber-800" data-testid="exclusion-problema">{problema}</p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={!datos || guardando || !!problema || !!fallo}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="exclusion-guardar">
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
